import React, { useEffect, useRef, useState } from "react";
import { api, downloadAuthed, uploadFile } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Download, DatabaseBackup, HardDriveDownload, HardDriveUpload, RotateCcw, Trash2, Server, ShieldAlert, Clock } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Backup() {
  const { can } = useAuth();
  const canManage = can("settings.edit");
  const [list, setList] = useState([]);
  const [range, setRange] = useState({ dfrom: "", dto: "" });
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind:'server'|'upload', id, name, file }
  const [sched, setSched] = useState({ auto_backup_enabled: false, auto_backup_time: "02:00", auto_backup_keep: 7 });
  const [schedBusy, setSchedBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/backup/server").then(setList).catch(() => {});
    api.get("/settings").then((s) => setSched({ auto_backup_enabled: !!s.auto_backup_enabled, auto_backup_time: s.auto_backup_time || "02:00", auto_backup_keep: s.auto_backup_keep ?? 7 })).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const qs = () => {
    const p = new URLSearchParams();
    if (range.dfrom) p.set("dfrom", range.dfrom);
    if (range.dto) p.set("dto", range.dto);
    const s = p.toString();
    return s ? `?${s}` : "";
  };

  const downloadLocal = () => downloadAuthed(`/backup/export${qs()}`, `rentledger-backup-${new Date().toISOString().slice(0, 10)}.json`);

  const createServer = async () => {
    setBusy(true);
    try {
      const r = await api.post("/backup/server", { label: label || undefined, dfrom: range.dfrom || undefined, dto: range.dto || undefined });
      toast.success(`Server backup created — ${r.total} records saved`);
      setLabel(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Backup failed"); }
    setBusy(false);
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const payload = JSON.parse(await f.text());
      const colls = payload.collections || {};
      const counts = {}; let total = 0;
      Object.entries(colls).forEach(([k, v]) => { if (Array.isArray(v) && v.length) { counts[k] = v.length; total += v.length; } });
      if (!total) return toast.error("This file has no data to restore.");
      setConfirm({ kind: "upload", name: f.name, file: f, counts, total });
    } catch { toast.error("Could not read that file — is it a valid backup JSON?"); }
  };

  const doRestore = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      let r;
      if (confirm.kind === "server") r = await api.post(`/backup/server/${confirm.id}/restore`, {});
      else r = await uploadFile("/backup/restore/upload", confirm.file);
      toast.success(`Restore complete — ${r.total} records restored. Reloading…`);
      setConfirm(null);
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); setBusy(false); }
  };

  const del = async (id) => { await api.del(`/backup/server/${id}`); toast.success("Backup deleted"); load(); };

  const saveSched = async () => {
    setSchedBusy(true);
    try { await api.put("/settings", { ...sched, auto_backup_keep: Math.min(60, Math.max(1, Number(sched.auto_backup_keep) || 7)) }); toast.success("Automatic backup schedule saved"); }
    catch { toast.error("Save failed"); }
    setSchedBusy(false);
  };

  if (!canManage) return <EmptyState icon={ShieldAlert} title="Restricted" hint="Only business owners and admins can manage backups." />;

  return (
    <div>
      <PageHeader title="Backup & Restore" subtitle="Save and restore all data for this business — to your computer or to the server" testid="backup-header" />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-slate-200 shadow-sm p-5" data-testid="backup-create-card">
          <div className="flex items-center gap-2 mb-4"><DatabaseBackup className="w-5 h-5 text-indigo-600" /><h3 className="font-heading font-semibold text-slate-800">Create a backup</h3></div>
          <p className="text-xs text-slate-500 mb-4">Optionally limit to a period (splits date-based records like invoices, payments & expenses). Leave blank to back up everything.</p>
          <div className="flex flex-wrap gap-3 mb-4">
            <div><label className="text-xs text-slate-500">From</label><Input type="date" value={range.dfrom} onChange={(e) => setRange({ ...range, dfrom: e.target.value })} data-testid="backup-from" className="h-9" /></div>
            <div><label className="text-xs text-slate-500">To</label><Input type="date" value={range.dto} onChange={(e) => setRange({ ...range, dto: e.target.value })} data-testid="backup-to" className="h-9" /></div>
            <div className="flex-1 min-w-[160px]"><label className="text-xs text-slate-500">Label (server backup)</label><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Before year-end cleanup" data-testid="backup-label" className="h-9" /></div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={downloadLocal} variant="outline" data-testid="download-local-btn"><HardDriveDownload className="w-4 h-4 mr-1" /> Download to computer</Button>
            <Button onClick={createServer} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-server-btn"><Server className="w-4 h-4 mr-1" /> Save to server</Button>
          </div>
        </Card>

        <Card className="border-slate-200 shadow-sm p-5" data-testid="restore-upload-card">
          <div className="flex items-center gap-2 mb-4"><HardDriveUpload className="w-5 h-5 text-amber-600" /><h3 className="font-heading font-semibold text-slate-800">Restore from a file</h3></div>
          <p className="text-xs text-slate-500 mb-4">Upload a previously downloaded <code>.json</code> backup. This <b>replaces</b> the current business data with the file's contents.</p>
          <input ref={fileRef} type="file" accept="application/json,.json" onChange={onPickFile} className="hidden" data-testid="restore-file-input" />
          <Button onClick={() => fileRef.current?.click()} variant="outline" className="text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="restore-upload-btn"><HardDriveUpload className="w-4 h-4 mr-1" /> Choose backup file…</Button>
        </Card>
      </div>

      <Card className="border-slate-200 shadow-sm p-5 mt-6" data-testid="auto-backup-card">
        <div className="flex items-center gap-2 mb-1"><Clock className="w-5 h-5 text-indigo-600" /><h3 className="font-heading font-semibold text-slate-800">Automatic backup</h3></div>
        <p className="text-xs text-slate-500 mb-4">The server automatically saves a full backup of this business every day at the time you set (IST). Automatic backups show an <b>AUTO</b> tag in the list below and old ones are pruned to your keep limit.</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-700 h-9"><Checkbox checked={sched.auto_backup_enabled} onCheckedChange={(v) => setSched({ ...sched, auto_backup_enabled: !!v })} data-testid="auto-backup-enabled" /> Enabled</label>
          <div><label className="text-xs text-slate-500">Time (IST)</label><Input type="time" value={sched.auto_backup_time} onChange={(e) => setSched({ ...sched, auto_backup_time: e.target.value })} data-testid="auto-backup-time" className="h-9 w-32" /></div>
          <div><label className="text-xs text-slate-500">Keep last</label><Input type="number" min="1" max="60" value={sched.auto_backup_keep} onChange={(e) => setSched({ ...sched, auto_backup_keep: e.target.value })} data-testid="auto-backup-keep" className="h-9 w-24" /></div>
          <Button onClick={saveSched} disabled={schedBusy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="auto-backup-save">Save schedule</Button>
        </div>
      </Card>

      <div className="mt-8">
        <h3 className="font-heading font-semibold text-slate-800 mb-3 flex items-center gap-2"><Server className="w-4 h-4 text-slate-500" /> Server backups</h3>
        {list.length === 0 ? <EmptyState icon={DatabaseBackup} title="No server backups yet" hint="Create one above to keep a restore point on the server." /> : (
          <Card className="border-slate-200 shadow-sm overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>Label</TableHead><TableHead>Created</TableHead><TableHead>By</TableHead><TableHead className="text-right">Records</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {list.map((b) => (
                  <TableRow key={b.id} data-testid={`backup-row-${b.id}`} className="hover:bg-slate-50/50">
                    <TableCell className="font-medium text-slate-800">{b.label}{b.type === "auto" && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 align-middle" data-testid={`auto-tag-${b.id}`}>AUTO</span>}</TableCell>
                    <TableCell className="text-xs">{fmtDate(b.created_at)} <span className="text-slate-400">{new Date(b.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span></TableCell>
                    <TableCell className="text-xs text-slate-500">{b.created_by}</TableCell>
                    <TableCell className="text-right font-medium">{b.total}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" title="Download" onClick={() => downloadAuthed(`/backup/server/${b.id}/download`, `backup-${b.id}.json`)} data-testid={`dl-${b.id}`}><Download className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-amber-600" title="Restore" onClick={() => setConfirm({ kind: "server", id: b.id, name: b.label, counts: b.counts, total: b.total })} data-testid={`restore-${b.id}`}><RotateCcw className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-rose-600" title="Delete" onClick={() => del(b.id)} data-testid={`del-${b.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      <Dialog open={!!confirm} onOpenChange={() => !busy && setConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2 text-rose-600"><ShieldAlert className="w-5 h-5" /> Confirm restore</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Restoring from <b>{confirm?.name}</b> will <b>permanently replace</b> all current data for this business with the backup contents. This cannot be undone.</p>
          {confirm?.counts && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 max-h-52 overflow-y-auto" data-testid="restore-preview">
              <p className="text-xs font-medium text-slate-600 mb-2">This backup contains {confirm.total} records:</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {Object.entries(confirm.counts).filter(([, n]) => n > 0).map(([k, n]) => (
                  <div key={k} className="flex justify-between"><span className="capitalize text-slate-500">{k.replace(/_/g, " ")}</span><span className="font-medium text-slate-700">{n}</span></div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={doRestore} disabled={busy} data-testid="confirm-restore-btn">{busy ? "Restoring…" : "Yes, restore & replace"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
